import React from 'react';
import { Box, Button, CircularProgress, Grid, Paper, Typography } from '@mui/material';
import ProfileField, { type ProfileFieldType } from './ProfileField';

export interface ProfileSectionType {
  title: string;
  icon?: React.ComponentType<{ size?: number }>;
  fields: ProfileFieldType[];
  hidden?: boolean;
  /** Extra content rendered below the fields grid */
  extra?: React.ReactNode;
}

interface ProfileSectionProps {
  section: ProfileSectionType;
  editing: boolean;
  saving?: boolean;
  onEdit?: () => void;
  onSave?: () => void;
  onCancel?: () => void;
  /** Hide the edit/save/cancel buttons for this section */
  hideActions?: boolean;
}

const ProfileSection: React.FC<ProfileSectionProps> = ({
  section,
  editing,
  saving,
  onEdit,
  onSave,
  onCancel,
  hideActions,
}) => {
  if (section.hidden) return null;

  const IconComponent = section.icon;

  return (
    <Paper
      sx={{
        p: 3,
        mb: 2,
        borderRadius: '12px',
        boxShadow: '0px 4px 12px rgba(0,0,0,0.08), 0px 2px 4px rgba(0,0,0,0.04)',
      }}
    >
      {/* Card header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {IconComponent && (
            <IconComponent size={20} />
          )}
          <Typography variant="h3" sx={{ fontSize: 20, fontWeight: 600 }}>
            {section.title}
          </Typography>
        </Box>

        {!hideActions && onEdit && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {editing ? (
              <>
                <Button variant="outlined" size="small" onClick={onCancel}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={onSave}
                  disabled={saving}
                >
                  {saving ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                  Save
                </Button>
              </>
            ) : (
              <Button variant="outlined" size="small" onClick={onEdit}>
                Edit
              </Button>
            )}
          </Box>
        )}
      </Box>

      {/* Fields grid */}
      <Grid container spacing={2}>
        {section.fields.map((field, idx) => (
          <Grid key={idx} size={{ xs: 12, sm: field.colSpan === 2 ? 12 : 6 }}>
            <ProfileField field={field} editing={editing} />
          </Grid>
        ))}
      </Grid>

      {/* Extra content */}
      {section.extra && (
        <Box sx={{ mt: 2 }}>
          {section.extra}
        </Box>
      )}
    </Paper>
  );
};

export default ProfileSection;
